import type { BrandSpecials } from '@iheartjane/data-access';
import { Placement } from '@iheartjane/dm-sdk';
import { FLAGS } from '@iheartjane/feature-flags';
import type { CardCarouselProps } from '@iheartjane/menu';
import {
  CardCarousel,
  MagicRowSubtitle,
  MOBILE_PRODUCT_CARD_WIDTH,
} from '@iheartjane/menu';
import { Typography } from '@iheartjane/reefer';
import type { Nullable } from '@iheartjane/types';
import { pluralize } from '@iheartjane/utils';
import { useMemo } from 'react';

import { BloomLink } from '@jane/bloom/components/bloomLink';
import { ProductCard } from '@jane/bloom/components/productCard/productCard';
import { useMemoizedRowInstance } from '@jane/bloom/hooks/dm/useMemoizedRowInstance';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import type { CustomLanguage } from '@jane/bloom/types/schemas/bloom/customLanguage';
import type {
  ProductApplicableIds,
  StoreSpecials,
} from '@jane/bloom/types/schemas/marketplace/storeSpecials';
import { getThemedProductCardWidth } from '@jane/bloom/utils/product/getThemedProductCardWidth';
import { createCustomRowWeightFilter } from '@jane/bloom/utils/specials/shouldShowWeight';
import type { JaneGold } from '@jane/bloom/viewModels/productDetail';
import { buildSingleProductSearchResultViewModel } from '@jane/bloom/viewModels/productSearchResult';

import styles from './menuRow.module.css';

type MenuRowProps = {
  brandSpecials: BrandSpecials;
  customLanguage: CustomLanguage;
  customRowId: number;
  customRowSlug: string;
  displayName: Nullable<string>;
  janeGold: JaneGold;
  mode?: CardCarouselProps['mode'];
  placement?: Placement;
  specials: Nullable<StoreSpecials>;
  stringifiedInstance: string;
};

export const MenuRow = ({
  brandSpecials,
  customLanguage,
  customRowId,
  customRowSlug,
  displayName,
  janeGold,
  mode = 'min-width',
  placement,
  specials,
  stringifiedInstance,
}: MenuRowProps) => {
  const { cart, featureFlags, pinnedStore, theme } = useRootLoaderData();
  const cardWidth = getThemedProductCardWidth(featureFlags, theme);
  const thematicRowContextFlag = featureFlags[FLAGS.c3ThematicRowsContext];
  const instance = useMemoizedRowInstance(stringifiedInstance);

  const weightFilter = useMemo(() => {
    const rules = pinnedStore.custom_rows?.find(
      (cr) => cr.id === customRowId
    )?.rules;
    return createCustomRowWeightFilter(rules);
  }, [pinnedStore.custom_rows, customRowId]);

  if (!instance || instance.products.length === 0) {
    return null;
  }

  const isNonCustomizableRow =
    placement === Placement.MenuDynamicRow ||
    placement === Placement.BuyAgainRow;

  const shouldShowViewAll =
    instance.products.length > 10 && !isNonCustomizableRow;

  const viewAllCustomRowLink = `/rows/${customRowId}/${customRowSlug}`;

  const totalProducts = isNonCustomizableRow
    ? instance.products.length
    : instance.totalProducts;

  return (
    <CardCarousel
      className="customRow"
      cardWidth={cardWidth}
      cardWidthMobile={MOBILE_PRODUCT_CARD_WIDTH}
      cardGap={24}
      cardGapMobile={16}
      mobileScrollPadding={16}
      mode={mode}
      name={displayName ?? ''}
      beforeHeader={
        <div className={styles['menuRowLabel']}>
          {isNonCustomizableRow ? (
            <Typography
              color="text-main"
              as="h2"
              variant="h2-semibold"
              maxLines={2}
              overflowWrap="anywhere"
            >
              {displayName}
            </Typography>
          ) : (
            <BloomLink to={viewAllCustomRowLink}>
              <Typography
                color="text-main"
                as="h2"
                variant="h2-semibold"
                maxLines={2}
                overflowWrap="anywhere"
              >
                {displayName}
              </Typography>
            </BloomLink>
          )}
          {thematicRowContextFlag && placement === Placement.MenuDynamicRow ? (
            <MagicRowSubtitle textColor="grays-mid" iconColor="primary" />
          ) : (
            <Typography color="grays-mid">
              {pluralize({ noun: 'product', number: totalProducts })}
            </Typography>
          )}
        </div>
      }
      afterHeader={
        shouldShowViewAll ? (
          <BloomLink
            aria-label={`View all ${displayName} products`}
            className={styles['viewAll']}
            to={viewAllCustomRowLink}
            variant="reefer-underline"
          >
            View all
          </BloomLink>
        ) : undefined
      }
    >
      {instance.products.map((product) => {
        const filteredPriceIds = weightFilter
          ? (product.attributes.available_weights?.filter((weight) =>
              weightFilter(weight, product)
            ) as ProductApplicableIds[])
          : undefined;

        return (
          <CardCarousel.Card key={product.objectId}>
            <ProductCard
              product={buildSingleProductSearchResultViewModel({
                applicablePriceIds: filteredPriceIds,
                bundleTag: featureFlags[FLAGS.c1BundleTag],
                cart,
                customLanguage,
                janeGold,
                pinnedStore,
                product: product.attributes,
                specials,
              })}
              brandSpecials={brandSpecials}
              smartProductInstance={product}
              customRowId={customRowId}
              isSponsored={product.isSponsored}
              selected={() => product.click()}
              weightFilter={weightFilter}
            />
          </CardCarousel.Card>
        );
      })}
    </CardCarousel>
  );
};
